import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, queryString } from '../api';
import { ErrorNotice, Notice, SelectField, Spinner, formatDate, formatMoney } from '../components/ui';
import type { AgmReport, MembershipYearSummary } from '../types';

/**
 * The figures for the AGM.
 *
 * Meant to be printed or read off a screen at the front of a room, so the
 * page is a document rather than a dashboard: no filters to fiddle with
 * beyond the year, everything visible at once, and a print stylesheet that
 * drops the navigation and the year picker so the paper carries only the
 * report. The browser's own Save as PDF does the rest — no dependency, and
 * it prints properly from a phone.
 */
export function AgmPage() {
  const [years, setYears] = useState<MembershipYearSummary[] | null>(null);
  const [yearId, setYearId] = useState('');
  const [report, setReport] = useState<AgmReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get<{ years: MembershipYearSummary[] }>('/membership-years')
      .then((result) => {
        setYears(result.years);
        const current = result.years.find((y) => y.is_current) ?? result.years[0];
        if (current) setYearId(String(current.id));
      })
      .catch(setError);
  }, []);

  useEffect(() => {
    if (yearId === '') return;
    setLoading(true);
    setError(null);
    api
      .get<AgmReport>(`/reports/agm${queryString({ membership_year_id: yearId })}`)
      .then(setReport)
      .catch((failure) => {
        setReport(null);
        setError(failure);
      })
      .finally(() => setLoading(false));
  }, [yearId]);

  return (
    <>
      <p className="muted small no-print">
        <Link to="/subscriptions">← Back to subscriptions</Link>
      </p>

      <div className="page-head no-print">
        <h1>AGM report</h1>
        <div className="head-actions">
          <SelectField label="" value={yearId} onChange={(e) => setYearId(e.target.value)}>
            {(years ?? []).map((year) => (
              <option key={year.id} value={year.id}>
                {year.year}
                {year.is_current ? ' (current)' : ''}
              </option>
            ))}
          </SelectField>
          <button type="button" className="primary" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      <ErrorNotice error={error} />
      {loading ? <Spinner label="Working out the figures" /> : null}

      {report !== null ? (
        <article className="report">
          <header className="report-head">
            <h1>Demo Beekeepers Association</h1>
            <p className="lede">
              Membership year {report.year.year} — opened {formatDate(report.year.opens_on)},
              subscriptions due {formatDate(report.year.due_on)}
            </p>
          </header>

          {report.checks.total_due_mismatches > 0 || report.checks.overpaid_subscriptions > 0 ? (
            <Notice kind="warning">
              {report.checks.total_due_mismatches > 0 ? (
                <>
                  {report.checks.total_due_mismatches} subscription
                  {report.checks.total_due_mismatches === 1 ? ' does' : 's do'} not add up: the total
                  charged is not the sum of the category fee, the BDI premium and any donations.
                  Every money figure below is suspect until that is looked at.{' '}
                </>
              ) : null}
              {report.checks.overpaid_subscriptions > 0 ? (
                <>
                  {report.checks.overpaid_subscriptions} subscription
                  {report.checks.overpaid_subscriptions === 1 ? ' has' : 's have'} been paid more
                  than once over.
                </>
              ) : null}
            </Notice>
          ) : null}

          <section>
            <h2>Membership</h2>
            <div className="figures">
              <Figure value={report.membership.members} label={`members in ${report.year.year}`} />
              <Figure value={report.membership.new} label="joined this year" />
              <Figure value={report.membership.renewed} label="renewed from last year" />
              <Figure value={report.membership.lapsed} label="did not renew" />
            </div>

            {report.membership.has_previous_year ? (
              <p>
                Of the members who held a subscription in {report.year.year - 1},{' '}
                <strong>{report.membership.retention_percent ?? 0}%</strong> renewed for{' '}
                {report.year.year}.
              </p>
            ) : (
              <p className="muted">
                There is no {report.year.year - 1} on file, so joiners, leavers and retention cannot
                be worked out for this year. The {report.membership.members} members shown held a
                subscription in {report.year.year}; how many were new is not something this can know.
              </p>
            )}

            <table className="members">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Members</th>
                  <th scope="col">Subscriptions</th>
                  <th scope="col">BDI</th>
                </tr>
              </thead>
              <tbody>
                {report.membership.by_category.map((row) => (
                  <tr key={row.category}>
                    <td data-label="Category">{row.category}</td>
                    <td data-label="Members">{row.members}</td>
                    <td data-label="Subscriptions">{formatMoney(row.subscriptions)}</td>
                    <td data-label="BDI">{formatMoney(row.bdi)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report.membership.waived > 0 ? (
              <p className="muted small">
                Includes {report.membership.waived} waived subscription
                {report.membership.waived === 1 ? '' : 's'}, counted as members but not as income.
              </p>
            ) : null}
          </section>

          <section>
            <h2>Money</h2>
            <div className="figures">
              <Figure value={formatMoney(report.money.received)} label="received" />
              <Figure value={formatMoney(report.money.billed)} label="charged" />
              <Figure value={formatMoney(report.money.outstanding)} label="still owed" />
            </div>

            <table className="members">
              <tbody>
                <tr>
                  <td data-label="">Subscriptions</td>
                  <td data-label="">{formatMoney(report.money.subscriptions)}</td>
                </tr>
                <tr>
                  <td data-label="">Bee disease insurance</td>
                  <td data-label="">{formatMoney(report.money.bdi)}</td>
                </tr>
                <tr>
                  <td data-label="">Donations</td>
                  <td data-label="">{formatMoney(report.money.donations)}</td>
                </tr>
                {report.money.waived > 0 ? (
                  <tr>
                    <td data-label="">Waived</td>
                    <td data-label="">−{formatMoney(report.money.waived)}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            <p className="muted small">
              Every figure is the amount frozen onto each subscription when it was taken out, not
              a recalculation at today's rates. Changing this year's fees does not move any of this.
              {report.money.waived > 0
                ? ' Waived subscriptions are taken out of what is still owed: they are a decision not to be paid, not a debt.'
                : ''}
            </p>

            {report.money.donations_by_fund.length > 0 ? (
              <>
                <h3>Donations by fund</h3>
                <table className="members">
                  <thead>
                    <tr>
                      <th scope="col">Fund</th>
                      <th scope="col">Gifts</th>
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.money.donations_by_fund.map((fund) => (
                      <tr key={fund.fund}>
                        <td data-label="Fund">{fund.fund}</td>
                        <td data-label="Gifts">{fund.gifts}</td>
                        <td data-label="Total">{formatMoney(fund.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}

            {report.money.by_method.length > 0 ? (
              <>
                <h3>How it arrived</h3>
                <table className="members">
                  <thead>
                    <tr>
                      <th scope="col">Method</th>
                      <th scope="col">Payments</th>
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.money.by_method.map((method) => (
                      <tr key={method.method}>
                        <td data-label="Method">{method.method}</td>
                        <td data-label="Payments">{method.payments}</td>
                        <td data-label="Total">{formatMoney(method.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
          </section>

          <section>
            <h2>Colonies</h2>
            <div className="figures">
              <Figure value={report.colonies.declared} label={`colonies declared for ${report.year.year}`} />
              <Figure value={report.colonies.insured_members} label="members with BDI cover" />
              <Figure value={report.colonies.referrals} label="over the top band" />
            </div>
            <p className="muted small">
              Declared figures are what members told us when they applied, and are what their BDI
              premiums were based on. Separately, {report.colonies.members_with_apiaries} member
              {report.colonies.members_with_apiaries === 1 ? ' has' : 's have'} recorded{' '}
              {report.colonies.apiaries_recorded} apiar
              {report.colonies.apiaries_recorded === 1 ? 'y' : 'ies'} holding{' '}
              {report.colonies.apiary_colonies} colonies as things stand today. The two are allowed
              to differ; hives get split.
            </p>
          </section>

          <section>
            <h2>Mentoring and training</h2>
            <div className="figures">
              <Figure value={report.mentoring.active_now} label="pairings running now" />
              <Figure value={report.mentoring.started} label={`started in ${report.year.year}`} />
              <Figure value={report.mentoring.completed} label="completed" />
              <Figure value={report.mentoring.mentors_willing} label="members willing to mentor" />
            </div>

            {report.courses.length > 0 ? (
              <table className="members">
                <thead>
                  <tr>
                    <th scope="col">Course</th>
                    <th scope="col">Came</th>
                    <th scope="col">Finished</th>
                  </tr>
                </thead>
                <tbody>
                  {report.courses.map((course) => (
                    <tr key={course.name}>
                      <td data-label="Course">{course.name}</td>
                      <td data-label="Came">{course.attendees}</td>
                      <td data-label="Finished">{course.completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No courses were recorded for {report.year.year}.</p>
            )}
          </section>

          {report.trend.length > 1 ? (
            <section>
              <h2>Recent years</h2>
              <table className="members">
                <thead>
                  <tr>
                    <th scope="col">Year</th>
                    <th scope="col">Members</th>
                    <th scope="col">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {report.trend.map((row) => (
                    <tr key={row.year}>
                      <td data-label="Year">{row.year}</td>
                      <td data-label="Members">{row.members}</td>
                      <td data-label="Received">{formatMoney(row.received)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <section>
            <h2>The roll as it stands today</h2>
            <p className="muted small">
              Not a {report.year.year} figure: this is how each member's record reads now. Somebody
              who resigned last month counts as resigned here, even if they were a paid-up member
              throughout {report.year.year}.
            </p>
            <div className="figures">
              {Object.entries(report.today).map(([status, count]) => (
                <Figure key={status} value={count} label={status} />
              ))}
            </div>
          </section>

          <footer className="report-foot muted small">
            Produced from the membership system on {formatDate(new Date().toISOString().slice(0, 10))}.
          </footer>
        </article>
      ) : null}
    </>
  );
}

function Figure({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="figure">
      <span className="figure-value">{value}</span>
      <span className="figure-label">{label}</span>
    </div>
  );
}
